import useGetNavigation from "@RHCommerceDev/hooks/useGetNavigation";
import { useLocalization } from "@RHCommerceDev/hooks/useLocalization";
import useUserPreferences from "@RHCommerceDev/hooks/useUserPreferences";
import parsedCatalogNavigation, {
  groupChildCategories
} from "@RHCommerceDev/utils/parseCatalogNavigationData";
import { usePageContent } from "customProviders/LocationProvider";
import React, { useCallback, useEffect, useState } from "react";
import { useScrollLock } from "@RHCommerceDev/hooks/useScrollLock";
import { useHeaderAtom } from "@RHCommerceDev/hooks/atoms/useHeaderAtom";
import { AnimatePresence, motion } from "motion/react";
import { useEnv } from "@RHCommerceDev/hooks/useEnv";
import { usePreloadImages } from "@RHCommerceDev/hooks/usePreloadImages";
import memoize from "@RHCommerceDev/utils/memoize";
import getAbsoluteUrl from "@RHCommerceDev/utils/getAbsoluteUrl";
import {
  PrimaryColumn,
  SecondaryColumn,
  TertiaryColumn
} from "./HamburgerDropdownColumns";
import { useCookies } from "react-cookie";
import { CATALOG_CATEGORIES } from "@RHCommerceDev/utils/constants";
import { cn } from "@RHCommerceDev/utils/cn";
import {
  CG_PREVIEW_ID_COOKIE,
  PG_PREVIEW_ID_COOKIE,
  PP_PREVIEW_ID_COOKIE
} from "pages/NextgenProductGallery/constants";

interface SelectedItem {
  sectionIndex: number;
  primaryIndex: number;
  secondaryIndex: number;
}

const DEFAULT_SELECTED_ITEM: SelectedItem = {
  sectionIndex: -1,
  primaryIndex: -1,
  secondaryIndex: -1
};

const DesktopHamburgerDropdown = () => {
  const locale = useLocalization();
  const env = useEnv();
  const { pageContent: pageContentObj } = usePageContent();

  const pageContent = React.useMemo(() => {
    return (
      ((pageContentObj?.hamburgerNav as Record<string, unknown>)
        ?.childCategories as Record<string, unknown>[]) || []
    );
  }, [pageContentObj]);

  const { catalogNavigation, saleCatalogNavigation } = useGetNavigation();
  const navigationData = parsedCatalogNavigation(
    catalogNavigation,
    saleCatalogNavigation,
    false
  );

  const [, , removeCookie] = useCookies([
    PG_PREVIEW_ID_COOKIE,
    PP_PREVIEW_ID_COOKIE,
    CG_PREVIEW_ID_COOKIE
  ]);

  const { country: selectedCountry, language: selectedLanguage } =
    useUserPreferences();

  const sections = React.useMemo(() => {
    const combinedSections = [navigationData];
    combinedSections.push(...pageContent);
    return combinedSections;
  }, [navigationData, pageContent]);

  const [selectedItem, setSelectedItem] = useState<SelectedItem>(
    DEFAULT_SELECTED_ITEM
  );

  const [isCountrySelectorOpen, setIsCountrySelectorOpen] =
    useState<boolean>(false);

  const { state, closeHamburgerDropdown } = useHeaderAtom();

  const isHamburgerDropdownOpen = state.isHamburgerDropdownOpen;

  const selectedSection = sections?.[selectedItem.sectionIndex];
  const selectedPrimaryItem =
    selectedSection?.childCategories?.[selectedItem.primaryIndex];
  const selectedSecondaryItem =
    selectedPrimaryItem?.childCategories?.[selectedItem.secondaryIndex];

  const groupedCategories = groupChildCategories(
    selectedSecondaryItem?.childCategories
  );

  const clearPreviewCookies = useCallback(() => {
    const previewCookies: any[] = [
      PG_PREVIEW_ID_COOKIE,
      PP_PREVIEW_ID_COOKIE,
      CG_PREVIEW_ID_COOKIE
    ];

    previewCookies.forEach(cookieName => {
      removeCookie(cookieName, { path: "/", domain: env.ENV_DOMAIN });
    });
  }, [removeCookie, env.ENV_DOMAIN]);

  //handle Primary Column button click
  const handlePrimaryItemClick = useCallback(
    (
      event: React.MouseEvent<HTMLButtonElement>,
      sectionIndex: number,
      primaryIndex: number
    ) => {
      const section = sections?.[sectionIndex];
      const primaryItem = section?.childCategories?.[primaryIndex];

      // isLink is true if item has no child categories
      const isLink = !primaryItem?.childCategories?.length;

      if (!isLink) {
        setIsCountrySelectorOpen(false);
        setSelectedItem({ sectionIndex, primaryIndex, secondaryIndex: -1 });
      } else {
        //analytics
        const displayName = primaryItem?.displayName;
        const target = event?.target as HTMLElement;
        const { id, className } = target;

        document?.body?.dispatchEvent(
          new CustomEvent("cta_click", {
            detail: {
              item: {
                fromHamburger: true,
                isHeaderNavigation: true,
                topCat:
                  sectionIndex > 3
                    ? "footer"
                    : sectionIndex === 0
                    ? displayName
                    : section?.displayName,
                parentCat:
                  sectionIndex > 3
                    ? section?.displayName
                    : displayName?.replaceAll("RH", "").trim(),
                cat: sectionIndex > 3 ? displayName : primaryItem?.targetUrl,
                catURL: primaryItem?.targetUrl,
                class: className,
                id: id
              }
            }
          })
        );

        //handle link click
        if (primaryItem?.linkType === "none") {
          window.open(primaryItem?.targetUrl ?? "", "_blank");
        } else {
          const url = getAbsoluteUrl(primaryItem?.targetUrl ?? "", locale);
          window.location.assign(url);
        }

        //close dropdown after navigating
        closeHamburgerDropdown();
      }
    },
    [sections, locale, closeHamburgerDropdown]
  );

  //handle Secondary Column button Click
  const handleSecondaryItemClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, secondaryIndex: number) => {
      const secondaryItem =
        selectedPrimaryItem?.childCategories?.[secondaryIndex];

      // isLink is true if item has no child categories
      const isLink = !secondaryItem?.childCategories?.length;

      if (!isLink) {
        setSelectedItem(prev => ({ ...prev, secondaryIndex }));
      } else {
        //analytics
        const target = event?.target as HTMLElement;
        const { id, className } = target;
        document?.body?.dispatchEvent(
          new CustomEvent("cta_click", {
            detail: {
              item: {
                fromHamburger: true,
                isHeaderNavigation: true,
                topCat: selectedPrimaryItem?.displayName,
                parentCat: secondaryItem?.displayName,
                cat: secondaryItem?.targetUrl,
                catURL: secondaryItem?.targetUrl,
                class: className,
                id: id
              }
            }
          })
        );

        //handle link click
        const isCatalogSection = selectedItem.sectionIndex === 0;
        const linkType = secondaryItem?.linkType;
        const targetUrl = secondaryItem?.targetUrl ?? "";

        if (isCatalogSection) {
          clearPreviewCookies();
          const isSaleCategory =
            selectedPrimaryItem?.displayName?.toLowerCase() ===
            CATALOG_CATEGORIES.SALE;

          if (isSaleCategory && linkType === "anchor") {
            const formattedUrl = targetUrl.startsWith("https://")
              ? targetUrl
              : `https://${targetUrl}`;
            window.open(formattedUrl, "_blank");
          } else {
            window.location.assign(getAbsoluteUrl(targetUrl, locale));
          }
        } else {
          if (linkType === "none") {
            window.open(targetUrl, "_blank");
          } else {
            window.location.assign(getAbsoluteUrl(targetUrl, locale));
          }
        }

        //close dropdown after navigating
        closeHamburgerDropdown();
      }
    },
    [
      selectedItem,
      selectedPrimaryItem,
      locale,
      closeHamburgerDropdown,
      clearPreviewCookies
    ]
  );

  //handle Tertiary Column button Click
  const handleTertiaryItemClick = useCallback(
    (
      event: React.MouseEvent<HTMLButtonElement>,
      groupIndex: number,
      tertiaryIndex: number
    ) => {
      const group = groupedCategories?.[groupIndex];
      const groupHeader = group?.header;
      const tertiaryItem = group?.children?.[tertiaryIndex];

      //analytics
      const target = event?.target as HTMLElement;
      const { id, className } = target;
      document?.body?.dispatchEvent(
        new CustomEvent("cta_click", {
          detail: {
            item: {
              fromHamburger: true,
              isHeaderNavigation: true,
              topCat: selectedPrimaryItem?.displayName,
              parentCat: selectedSecondaryItem?.displayName,
              cat: groupHeader
                ? groupHeader?.displayName
                : tertiaryItem?.displayName,
              catURL: groupHeader
                ? tertiaryItem?.displayName
                : tertiaryItem?.targetUrl,
              class: className,
              id: id
            }
          }
        })
      );

      const isCatalogSection = selectedItem.sectionIndex === 0;
      const linkType = tertiaryItem?.linkType;
      const targetUrl = tertiaryItem?.targetUrl ?? "";

      if (isCatalogSection) {
        clearPreviewCookies();
        const isSaleCategory =
          selectedPrimaryItem?.displayName?.toLowerCase() ===
          CATALOG_CATEGORIES.SALE;

        if (isSaleCategory && linkType === "anchor") {
          const formattedUrl = targetUrl.startsWith("https://")
            ? targetUrl
            : `https://${targetUrl}`;
          window.open(formattedUrl, "_blank");
        } else {
          window.location.assign(getAbsoluteUrl(targetUrl, locale));
        }
      } else {
        if (linkType === "none") {
          window.open(targetUrl, "_blank");
        } else {
          window.location.assign(getAbsoluteUrl(targetUrl, locale));
        }
      }

      //close dropdown after navigating
      closeHamburgerDropdown();
    },
    [
      locale,
      selectedItem,
      groupedCategories,
      selectedPrimaryItem,
      selectedSecondaryItem,
      closeHamburgerDropdown,
      clearPreviewCookies
    ]
  );

  //handle country selector click in primary column
  const handleCountrySelectorClick = useCallback(() => {
    setIsCountrySelectorOpen(prev => !prev);
    setSelectedItem(DEFAULT_SELECTED_ITEM);
  }, []);

  const dropdownHeight = state.headerRect
    ? `calc(100% - ${state.headerRect?.height}px)`
    : undefined;

  const imgPublicUrl = (env.PUBLIC_URL || "").replace(new RegExp(/\/?$/gm), "");

  // Preload Waterworks logo image
  usePreloadImages(["/img/WaterworksLogoWhite.svg"]);

  //lock and unlock body scroll
  const { lock, unlock } = useScrollLock({
    autoLock: false,
    widthReflow: false
  });

  //Resets selected state and body scroll
  useEffect(() => {
    if (isHamburgerDropdownOpen) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      lock();
    } else {
      setSelectedItem(DEFAULT_SELECTED_ITEM);
      setIsCountrySelectorOpen(false);

      unlock();
    }
  }, [isHamburgerDropdownOpen, lock, unlock]);

  return (
    <>
      <AnimatePresence mode="wait">
        {isHamburgerDropdownOpen && (
          <motion.div
            key="dropdown"
            initial={{ y: "-100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "-100%", opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className={cn(
              "fixed inset-0 z-[1099]",
              "flex flex-col justify-end",
              "h-full w-full",
              "bg-rh-mocha text-white"
            )}
          >
            <div
              className={cn(
                "flex",
                "gap-[75px] lg:gap-20",
                "px-10 xl:px-20",
                "md:pt-1.5"
              )}
              style={{
                height: dropdownHeight
              }}
            >
              {/* PRIMARY COLUMN */}
              <motion.div
                key={String(isHamburgerDropdownOpen)}
                initial={{ opacity: 0 }}
                animate={{
                  opacity: 1,
                  transition: { delay: 0.3, duration: 0.3 }
                }}
                exit={{ opacity: 0, transition: { duration: 0.1 } }}
                className="flex"
              >
                <PrimaryColumn
                  sections={sections}
                  selectedItem={selectedItem}
                  onPrimaryItemClick={handlePrimaryItemClick}
                  onCountrySelectorClick={handleCountrySelectorClick}
                  selectedCountry={selectedCountry}
                  selectedLanguage={selectedLanguage
                    .split("-")[0]
                    .toUpperCase()}
                  isCountrySelectorOpen={isCountrySelectorOpen}
                />
              </motion.div>

              {/* SECONDARY COLUMN */}
              {(selectedItem.primaryIndex !== -1 || isCountrySelectorOpen) && (
                <motion.div
                  key={`${selectedItem.sectionIndex}-${selectedItem.primaryIndex}`}
                  initial={{ opacity: 0 }}
                  animate={{
                    opacity: 1,
                    transition: { duration: 0.3 }
                  }}
                  exit={{ opacity: 0, transition: { duration: 0.1 } }}
                  className="flex"
                >
                  <SecondaryColumn
                    sections={sections}
                    selectedItem={selectedItem}
                    onSecondaryItemClick={handleSecondaryItemClick}
                    selectedCountry={selectedCountry}
                    selectedLanguage={selectedLanguage}
                    isCountrySelectorOpen={isCountrySelectorOpen}
                    variant="desktop"
                    waterWorksUrl={`${imgPublicUrl}/img/WaterworksLogoWhite.svg`}
                  />
                </motion.div>
              )}

              {/* TERTIARY COLUMN */}
              {selectedItem.secondaryIndex !== -1 && (
                <motion.div
                  key={selectedItem.secondaryIndex}
                  initial={{ opacity: 0 }}
                  animate={{
                    opacity: 1,
                    transition: { duration: 0.3 }
                  }}
                  exit={{ opacity: 0, transition: { duration: 0.1 } }}
                  className="flex"
                >
                  <TertiaryColumn
                    sections={sections}
                    selectedItem={selectedItem}
                    groups={groupedCategories}
                    onTertiaryItemClick={handleTertiaryItemClick}
                    variant="desktop"
                    waterWorksUrl={`${imgPublicUrl}/img/WaterworksLogoWhite.svg`}
                  />
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export const MemoizedDesktopHamburgerDropdown = memoize(
  DesktopHamburgerDropdown
);
